import React, { useState, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import SideBar from '../../components/Topbar';
import { HTMLCourseNavProvider } from "../../components/SidebarCourseContext";
import './assets/styles/HTMLLesson1.css';
import { CopyBlock, dracula } from "react-code-blocks";

// Import sophisticated icons
import { 
  FaCode,
  FaCheck,
  FaArrowRight,
  FaPlay,
  FaBookOpen,
  FaLayerGroup,
  FaTable,
  FaTh,
  FaTint,
  FaExpand,
  FaObjectGroup,
  FaExclamationTriangle,
  FaSort,
  FaChartBar
} from 'react-icons/fa';

const HTMLLesson16 = () => {
  const [activeSection, setActiveSection] = useState(0);
  const [isCompleted, setIsCompleted] = useState(false);
  const [viewedSections, setViewedSections] = useState(new Set([0]));
  const sectionRefs = useRef([]);
  const observerRef = useRef(null);

  const [language] = useState("html");
  const [lineNumbers] = useState(true);

  // Video explanation URL
  const videoUrl = "https://app.codeflarelimited.com/watch-video/html-course";

  const code1 = `<!-- Basic table structure -->
<table>
  <thead>
    <tr>
      <th>Name</th>
      <th>Department</th>
      <th>Salary</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>John Smith</td>
      <td>Engineering</td>
      <td>$85,000</td>
    </tr>
    <tr>
      <td>Sarah Johnson</td>
      <td>Marketing</td>
      <td>$72,000</td>
    </tr>
    <tr>
      <td>Mike Chen</td>
      <td>Sales</td>
      <td>$68,000</td>
    </tr>
  </tbody>
</table>`;

  const code2 = `<!-- Complex table with multiple sections -->
<table>
  <caption>Company Employee Directory 2024</caption>
  <colgroup>
    <col span="1" style="background-color: #f3f4f6">
    <col span="2" style="background-color: white">
  </colgroup>
  <thead>
    <tr>
      <th scope="col">Employee ID</th>
      <th scope="col">Full Name</th>
      <th scope="col">Position</th>
      <th scope="col">Department</th>
      <th scope="col">Join Date</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <th scope="row">EMP-001</th>
      <td>John Smith</td>
      <td>Senior Developer</td>
      <td>Engineering</td>
      <td>2022-03-15</td>
    </tr>
    <tr>
      <th scope="row">EMP-002</th>
      <td>Sarah Johnson</td>
      <td>Marketing Manager</td>
      <td>Marketing</td>
      <td>2021-08-22</td>
    </tr>
  </tbody>
  <tfoot>
    <tr>
      <td colspan="4" style="text-align: right">Total Employees:</td>
      <td><strong>2</strong></td>
    </tr>
  </tfoot>
</table>`;

  const code3 = `<!-- Table with colspan -->
<table border="1">
  <thead>
    <tr>
      <th>Product</th>
      <th>Q1 2024</th>
      <th>Q2 2024</th>
      <th>Total</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td>Web Design Suite</td>
      <td>$45,000</td>
      <td>$52,000</td>
      <td>$97,000</td>
    </tr>
    <tr>
      <td colspan="3" style="text-align: center; background: #f0f9ff">
        <strong>Subtotal - Software Products</strong>
      </td>
      <td><strong>$97,000</strong></td>
    </tr>
    <tr>
      <td>Consulting Services</td>
      <td>$38,000</td>
      <td>$42,000</td>
      <td>$80,000</td>
    </tr>
    <tr>
      <td colspan="3" style="text-align: center; background: #f0f9ff">
        <strong>Subtotal - Services</strong>
      </td>
      <td><strong>$80,000</strong></td>
    </tr>
    <tr>
      <td colspan="3" style="text-align: right; background: #dcfce7">
        <strong>Grand Total</strong>
      </td>
      <td style="background: #dcfce7"><strong>$177,000</strong></td>
    </tr>
  </tbody>
</table>`;

  const code4 = `<!-- Table with rowspan -->
<table border="1">
  <thead>
    <tr>
      <th>Department</th>
      <th>Team Lead</th>
      <th>Projects</th>
      <th>Team Members</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <td rowspan="3" style="vertical-align: middle; background: #fef3c7">
        <strong>Engineering</strong>
      </td>
      <td rowspan="3" style="vertical-align: middle">
        John Smith
      </td>
      <td>Website Redesign</td>
      <td>5 members</td>
    </tr>
    <tr>
      <td>Mobile App</td>
      <td>3 members</td>
    </tr>
    <tr>
      <td>API Development</td>
      <td>4 members</td>
    </tr>
    <tr>
      <td rowspan="2" style="vertical-align: middle; background: #f3e8ff">
        <strong>Marketing</strong>
      </td>
      <td rowspan="2" style="vertical-align: middle">
        Sarah Johnson
      </td>
      <td>Social Media Campaign</td>
      <td>2 members</td>
    </tr>
    <tr>
      <td>Content Strategy</td>
      <td>3 members</td>
    </tr>
  </tbody>
</table>`;

  const code5 = `<!-- Accessible table with proper semantics -->
<table aria-describedby="table-description">
  <caption>Monthly Sales Report - 2024</caption>
  <thead>
    <tr>
      <th scope="col" id="month">Month</th>
      <th scope="col" id="product-a">Product A</th>
      <th scope="col" id="product-b">Product B</th>
      <th scope="col" id="product-c">Product C</th>
      <th scope="col" id="total">Total</th>
    </tr>
  </thead>
  <tbody>
    <tr>
      <th scope="row" id="january">January</th>
      <td headers="january product-a">$12,500</td>
      <td headers="january product-b">$8,300</td>
      <td headers="january product-c">$15,200</td>
      <td headers="january total">$36,000</td>
    </tr>
    <tr>
      <th scope="row" id="february">February</th>
      <td headers="february product-a">$14,200</td>
      <td headers="february product-b">$9,100</td>
      <td headers="february product-c">$16,800</td>
      <td headers="february total">$40,100</td>
    </tr>
  </tbody>
  <tfoot>
    <tr>
      <th scope="row">Quarter Total</th>
      <td>$26,700</td>
      <td>$17,400</td>
      <td>$32,000</td>
      <td><strong>$76,100</strong></td>
    </tr>
  </tfoot>
</table>

<div id="table-description" class="sr-only">
  This table shows monthly sales figures for three products across the first quarter of 2024, including totals for each month and the quarter.
</div>`;

  const code6 = `<!DOCTYPE html>
<html>
<head>
  <style>
    /* Modern table styling */
    .data-table {
      width: 100%;
      border-collapse: collapse;
      margin: 2rem 0;
      font-family: 'Segoe UI', Tahoma, Geneva, Verdana, sans-serif;
      box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);
      border-radius: 8px;
      overflow: hidden;
    }
    
    .data-table caption {
      font-size: 1.5rem;
      font-weight: bold;
      margin-bottom: 1rem;
      color: #1f2937;
      text-align: left;
    }
    
    .data-table thead {
      background: linear-gradient(135deg, #3b82f6, #1d4ed8);
      color: white;
    }
    
    .data-table th {
      padding: 1rem;
      text-align: left;
      font-weight: 600;
      border-bottom: 2px solid #e5e7eb;
    }
    
    .data-table td {
      padding: 0.75rem 1rem;
      border-bottom: 1px solid #e5e7eb;
    }
    
    .data-table tbody tr {
      transition: background-color 0.2s ease;
    }
    
    .data-table tbody tr:hover {
      background-color: #f8fafc;
    }
    
    .data-table tfoot {
      background-color: #f1f5f9;
      font-weight: bold;
    }
    
    /* Zebra striping */
    .data-table tbody tr:nth-child(even) {
      background-color: #fafafa;
    }
    
    .data-table tbody tr:nth-child(even):hover {
      background-color: #f3f4f6;
    }
    
    /* Status indicators */
    .status-active {
      color: #059669;
      font-weight: 600;
    }
    
    .status-pending {
      color: #d97706;
      font-weight: 600;
    }
    
    .status-inactive {
      color: #6b7280;
      font-weight: 600;
    }
    
    /* Responsive table */
    .table-container {
      overflow-x: auto;
      margin: 2rem 0;
    }
  </style>
</head>
<body>
  <div class="table-container">
    <table class="data-table">
      <caption>Project Management Dashboard</caption>
      <thead>
        <tr>
          <th scope="col">Project Name</th>
          <th scope="col">Team Lead</th>
          <th scope="col">Department</th>
          <th scope="col">Start Date</th>
          <th scope="col">Deadline</th>
          <th scope="col">Status</th>
          <th scope="col">Progress</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Website Redesign</td>
          <td>John Smith</td>
          <td>Engineering</td>
          <td>2024-01-15</td>
          <td>2024-06-30</td>
          <td class="status-active">Active</td>
          <td>65%</td>
        </tr>
        <tr>
          <td>Mobile App Development</td>
          <td>Sarah Johnson</td>
          <td>Engineering</td>
          <td>2024-02-01</td>
          <td>2024-08-15</td>
          <td class="status-active">Active</td>
          <td>45%</td>
        </tr>
        <tr>
          <td>Marketing Campaign</td>
          <td>Mike Chen</td>
          <td>Marketing</td>
          <td>2024-03-01</td>
          <td>2024-05-31</td>
          <td class="status-pending">Pending</td>
          <td>20%</td>
        </tr>
        <tr>
          <td>Sales Training Program</td>
          <td>Emily Davis</td>
          <td>Sales</td>
          <td>2024-01-10</td>
          <td>2024-04-30</td>
          <td class="status-active">Active</td>
          <td>85%</td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="5" style="text-align: right">Total Projects:</td>
          <td colspan="2"><strong>4 projects</strong></td>
        </tr>
        <tr>
          <td colspan="5" style="text-align: right">Active Projects:</td>
          <td colspan="2"><strong>3 projects (75%)</strong></td>
        </tr>
      </tfoot>
    </table>
  </div>

  <!-- Complex table with colspan and rowspan -->
  <div class="table-container">
    <table class="data-table">
      <caption>Department Budget Allocation 2024</caption>
      <thead>
        <tr>
          <th rowspan="2">Department</th>
          <th rowspan="2">Manager</th>
          <th colspan="4" style="text-align: center">Quarterly Budget (in $000s)</th>
          <th rowspan="2">Annual Total</th>
        </tr>
        <tr>
          <th>Q1</th>
          <th>Q2</th>
          <th>Q3</th>
          <th>Q4</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>Engineering</td>
          <td>John Smith</td>
          <td>250</td>
          <td>275</td>
          <td>300</td>
          <td>325</td>
          <td><strong>1,150</strong></td>
        </tr>
        <tr>
          <td>Marketing</td>
          <td>Sarah Johnson</td>
          <td>150</td>
          <td>175</td>
          <td>200</td>
          <td>225</td>
          <td><strong>750</strong></td>
        </tr>
        <tr>
          <td>Sales</td>
          <td>Mike Chen</td>
          <td>200</td>
          <td>225</td>
          <td>250</td>
          <td>275</td>
          <td><strong>950</strong></td>
        </tr>
      </tbody>
      <tfoot>
        <tr>
          <td colspan="2" style="text-align: right"><strong>Company Total:</strong></td>
          <td><strong>600</strong></td>
          <td><strong>675</strong></td>
          <td><strong>750</strong></td>
          <td><strong>825</strong></td>
          <td><strong>2,850</strong></td>
        </tr>
      </tfoot>
    </table>
  </div>
</body>
</html>`;

  const sections = [
    {
      title: "HTML Tables Introduction",
      content: "Tables organize and display data in a structured grid format, perfect for presenting complex information clearly",
      keyPoints: [
        "<table> defines the table container",
        "<tr> creates table rows",
        "<td> defines standard data cells",
        "<th> creates header cells (bold and centered)"
      ],
      icon: <FaTable className="section-icon" />,
      color: "#2563eb",
      code: code1
    },
    {
      title: "Advanced Table Structure", 
      content: "Use semantic table sections for better organization, accessibility, and styling control",
      keyPoints: [
        "<thead> for header rows",
        "<tbody> for main content",
        "<tfoot> for summary rows",
        "<caption> for table description"
      ],
      icon: <FaTh className="section-icon" />,
      color: "#059669",
      code: code2
    },
    {
      title: "Column Spanning with Colspan",
      content: "Merge cells horizontally across multiple columns to create complex layouts and group related data",
      keyPoints: [
        "colspan='number' attribute merges columns",
        "Use for headers spanning multiple columns",
        "Perfect for subtotals and section headers",
        "Adjusts table column count automatically"
      ],
      icon: <FaExpand className="section-icon" />,
      color: "#dc2626",
      code: code3
    },
    {
      title: "Row Spanning with Rowspan",
      content: "Merge cells vertically across multiple rows to represent hierarchical or grouped data relationships",
      keyPoints: [
        "rowspan='number' attribute merges rows",
        "Use for data that applies to multiple rows",
        "Ideal for categories and groups",
        "Maintains vertical alignment with vertical-align"
      ],
      icon: <FaTint className="section-icon" />,
      color: "#7c3aed",
      code: code4
    },
    {
      title: "Accessible Tables",
      content: "Implement accessibility features to make tables usable for screen readers and assistive technologies",
      keyPoints: [
        "Use scope attribute for headers",
        "Add captions for table descriptions",
        "Implement headers and id attributes",
        "Use ARIA labels for complex tables"
      ],
      icon: <FaSort className="section-icon" />,
      color: "#d97706",
      code: code5
    }
  ];

  // Intersection Observer for scroll detection
  useEffect(() => {
    const options = {
      root: null,
      rootMargin: '-10% 0px -20% 0px',
      threshold: 0.6
    };

    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        const index = sectionRefs.current.indexOf(entry.target);
        
        if (entry.isIntersecting) {
          setActiveSection(index);
          setViewedSections(prev => new Set([...prev, index]));
        }
      });
    }, options);

    sectionRefs.current.forEach((section) => {
      if (section) {
        observerRef.current.observe(section);
      }
    });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  const addSectionRef = (element, index) => {
    sectionRefs.current[index] = element;
  };

  const getCompletionPercentage = () => {
    const totalSections = sections.length + 3;
    const viewedCount = viewedSections.size;
    return Math.round((viewedCount / totalSections) * 100);
  };

  return (
    <HTMLCourseNavProvider>
      <div className="html-lesson-container">
        <SideBar />
        
        <div className="html-lesson-main">
          {/* Header Section */}
          <div className="html-lesson-header">
            <div className="html-header-content">
              <div className="html-lesson-badge">
                <FaBookOpen className="html-badge-icon" />
                Lesson 16 • {getCompletionPercentage()}% Viewed
              </div>
              <h1 className="html-lesson-title">HTML Tables Mastery</h1>
              <p className="html-lesson-subtitle">
                Master data organization with comprehensive table structures, spanning, and accessibility features
              </p>
              
              {/* Progress Bar */}
              <div className="html-completion-progress">
                <div 
                  className="html-completion-fill"
                  style={{ width: `${getCompletionPercentage()}%` }}
                ></div>
                <div className="html-progress-labels">
                  <span>Getting Started</span>
                  <span>Complete</span>
                </div>
              </div>
            </div>
          </div>

          {/* Progress Indicator */}
          <div className="html-progress-indicator">
            {sections.map((section, index) => (
              <div 
                key={index}
                className={`html-progress-step ${viewedSections.has(index) ? 'html-viewed' : ''} ${index === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-step-number">
                  {viewedSections.has(index) ? '✓' : index + 1}
                </div>
                <div className="html-step-label">{section.title.split(' ')[0]}</div>
                {index < sections.length - 1 && <div className="html-step-connector"></div>}
              </div>
            ))}
          </div>

          {/* Visual Table Demonstration */}
          <div 
            className="html-visual-class"
            ref={(el) => addSectionRef(el, 0)}
          >
            <div className="html-visual-header">
              <div className="html-code-badge">
                <FaLayerGroup className="html-badge-icon" />
                Table Structure Power
              </div>
              <h3 className="html-visual-title">
                From Simple Data Grids to Complex Information Layouts
              </h3>
            </div>
            
            <div className="html-class-demo">
              <div className="html-demo-code">
                <h4>Basic Table Syntax</h4>
                <div className="html-code-block-small">
                  <CopyBlock
                    language="html"
                    text={`<table>
  <tr>
    <th>Header 1</th>
    <th>Header 2</th>
  </tr>
  <tr>
    <td>Data 1</td>
    <td>Data 2</td>
  </tr>
</table>`}
                    showLineNumbers={false}
                    theme={dracula}
                    wrapLines={true}
                    codeBlock
                  />
                </div>
              </div>
              
              <div className="html-demo-visual">
                <h4>Live Table Example</h4>
                <div className="html-table-preview">
                  <table className="preview-table">
                    <thead>
                      <tr>
                        <th>Product</th>
                        <th>Price</th>
                        <th>Stock</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr>
                        <td>Laptop</td>
                        <td>$999</td>
                        <td>15</td>
                      </tr>
                      <tr>
                        <td>Mouse</td>
                        <td>$25</td>
                        <td>42</td>
                      </tr>
                      <tr>
                        <td>Keyboard</td>
                        <td>$75</td>
                        <td>28</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            
            <div className="html-demo-note">
              <FaExclamationTriangle className="html-note-icon" />
              <p><strong>Remember:</strong> Always use semantic table structure with thead, tbody, and th elements for accessibility!</p>
            </div>
          </div>

          {/* Content Sections */}
          <div className="html-content-sections">
            {sections.map((section, index) => (
              <div 
                key={index}
                ref={(el) => addSectionRef(el, index + 1)}
                className={`html-content-card ${viewedSections.has(index + 1) ? 'html-viewed' : ''} ${(index + 1) === activeSection ? 'html-active' : ''}`}
              >
                <div className="html-card-badge">
                  {viewedSections.has(index + 1) ? '✓ Viewed' : `Part ${index + 1}`}
                </div>
                
                <div className="html-card-header">
                  <div className="html-card-icon" style={{ backgroundColor: section.color }}>
                    {section.icon}
                  </div>
                  <div className="html-card-text">
                    <h2 className="html-card-title">{section.title}</h2>
                    <p className="html-card-content">{section.content}</p>
                    
                    <div className="html-key-points">
                      <h4>Key Points:</h4>
                      <ul>
                        {section.keyPoints.map((point, idx) => (
                          <li key={idx}>{point}</li>
                        ))}
                      </ul>
                    </div>
                  </div>
                </div>

                {/* Code Example */}
                {section.code && (
                  <div className="html-section-code">
                    <div className="html-code-badge">
                      <FaCode className="html-badge-icon" />
                      Example
                    </div>
                    <div className="html-code-block-small">
                      <CopyBlock
                        language={language}
                        text={section.code}
                        showLineNumbers={lineNumbers}
                        theme={dracula}
                        wrapLines={true}
                        codeBlock
                      />
                    </div>
                  </div>
                )}
                
                <div className="html-card-actions">
                  <a 
                    href={videoUrl} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="html-action-btn"
                  >
                    <FaPlay className="html-btn-icon" />
                    Watch Video Explanation
                  </a>
                </div>
              </div>
            ))}
          </div>

          {/* Table Elements Comparison */}
          <div 
            className="html-comparison-section"
            ref={(el) => addSectionRef(el, sections.length + 1)}
          >
            <div className="html-comparison-header">
              <div className="html-code-badge">
                <FaTable className="html-badge-icon" />
                Table Elements Guide
              </div>
              <h3 className="html-comparison-title">
                HTML Table Elements and Their Purposes
              </h3>
            </div>
            
            <div className="html-comparison-table">
              <table>
                <thead>
                  <tr>
                    <th>Element</th>
                    <th>Purpose</th>
                    <th>Attributes</th>
                    <th>Best For</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td><code>&lt;table&gt;</code></td>
                    <td>Table container</td>
                    <td>border, width, cellspacing</td>
                    <td>All table structures</td>
                  </tr>
                  <tr>
                    <td><code>&lt;tr&gt;</code></td>
                    <td>Table row</td>
                    <td>-</td>
                    <td>Organizing horizontal data</td>
                  </tr>
                  <tr>
                    <td><code>&lt;td&gt;</code></td>
                    <td>Data cell</td>
                    <td>colspan, rowspan</td>
                    <td>Regular table data</td>
                  </tr>
                  <tr>
                    <td><code>&lt;th&gt;</code></td>
                    <td>Header cell</td>
                    <td>scope, colspan, rowspan</td>
                    <td>Column/row headers</td>
                  </tr>
                  <tr>
                    <td><code>&lt;thead&gt;</code></td>
                    <td>Header section</td>
                    <td>-</td>
                    <td>Column headers</td>
                  </tr>
                  <tr>
                    <td><code>&lt;tbody&gt;</code></td>
                    <td>Body section</td>
                    <td>-</td>
                    <td>Main table data</td>
                  </tr>
                  <tr>
                    <td><code>&lt;tfoot&gt;</code></td>
                    <td>Footer section</td>
                    <td>-</td>
                    <td>Summaries, totals</td>
                  </tr>
                  <tr>
                    <td><code>&lt;caption&gt;</code></td>
                    <td>Table description</td>
                    <td>-</td>
                    <td>Accessibility, context</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          {/* Table Best Practices */}
          <div 
            className="html-practices-section"
            ref={(el) => addSectionRef(el, sections.length + 2)}
          >
            <div className="html-practices-header">
              <div className="html-code-badge">
                <FaCheck className="html-badge-icon" />
                Table Best Practices
              </div>
              <h3 className="html-practices-title">
                Professional Table Implementation Guidelines
              </h3>
            </div>
            
            <div className="html-practices-grid">
              <div className="html-practice-card html-do">
                <h4>✅ Do These</h4>
                <ul>
                  <li>Use semantic table structure (thead, tbody)</li>
                  <li>Include captions for table context</li>
                  <li>Use scope attribute for header cells</li>
                  <li>Make tables responsive for mobile devices</li>
                  <li>Provide adequate padding and spacing</li>
                </ul>
              </div>
              
              <div className="html-practice-card html-dont">
                <h4>❌ Avoid These</h4>
                <ul>
                  <li>Using tables for page layout (use CSS Grid/Flexbox)</li>
                  <li>Nesting tables excessively</li>
                  <li>Using empty cells for spacing (use CSS)</li>
                  <li>Forgetting accessibility features</li>
                  <li>Creating overly complex table structures</li>
                </ul>
              </div>
            </div>
          </div>

          {/* Spanning Techniques */}
          <div 
            className="html-multiple-classes"
            ref={(el) => addSectionRef(el, sections.length + 3)}
          >
            <div className="html-multiple-header">
              <div className="html-code-badge">
                <FaObjectGroup className="html-badge-icon" />
                Cell Spanning Techniques
              </div>
              <h3 className="html-multiple-title">
                Mastering Colspan and Rowspan for Complex Layouts
              </h3>
            </div>
            
            <div className="html-multiple-content">
              <div className="html-multiple-explanation">
                <h4>Spanning Strategies:</h4>
                <ul>
                  <li><strong>Colspan:</strong> Merge cells horizontally
                    <ul>
                      <li>Use for headers spanning multiple columns</li>
                      <li>Perfect for grouped data categories</li>
                      <li>Adjust total column count in row</li>
                    </ul>
                  </li>
                  <li><strong>Rowspan:</strong> Merge cells vertically
                    <ul>
                      <li>Use for data applying to multiple rows</li>
                      <li>Ideal for hierarchical information</li>
                      <li>Set vertical-align for proper positioning</li>
                    </ul>
                  </li>
                  <li><strong>Combined Spanning:</strong> Use both for complex layouts
                    <ul>
                      <li>Create sophisticated data relationships</li>
                      <li>Maintain proper table structure</li>
                      <li>Ensure accessibility with proper markup</li>
                    </ul>
                  </li>
                </ul>
              </div>
              
              <div className="html-spanning-examples">
                <h4>Spanning Examples:</h4>
                <div className="spanning-demo">
                  <div className="spanning-item">
                    <div className="spanning-icon">→</div>
                    <span>Colspan: Horizontal merging</span>
                  </div>
                  <div className="spanning-item">
                    <div className="spanning-icon">↓</div>
                    <span>Rowspan: Vertical merging</span>
                  </div>
                  <div className="spanning-item">
                    <div className="spanning-icon">↘</div>
                    <span>Both: Complex layouts</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Comprehensive Example */}
          <div 
            className="html-code-example"
            ref={(el) => addSectionRef(el, sections.length + 4)}
          >
            <div className="html-code-header">
              <div className="html-code-badge">
                <FaCode className="html-badge-icon" />
                Complete Example
              </div>
              <h3 className="html-code-title">
                Professional Table System Implementation
              </h3>
            </div>
            <div className="html-code-block">
              <CopyBlock
                language={language}
                text={code6}
                showLineNumbers={lineNumbers}
                theme={dracula}
                wrapLines={true}
                codeBlock
              />
            </div>
            <div className="html-code-actions">
              <a 
                href={videoUrl} 
                target="_blank" 
                rel="noopener noreferrer"
                className="html-code-btn html-primary"
              >
                Watch Complete Table Implementation Guide
              </a>
            </div>
          </div>

          {/* Action Section */}
          <div 
            className="html-action-section"
            ref={(el) => addSectionRef(el, sections.length + 5)}
          >
            <div className="html-action-header">
              <h3>Mastered HTML tables?</h3>
              <div className="html-completion-badge">
                {getCompletionPercentage()}% of content viewed
              </div>
            </div>
            
            <div className="html-action-content">
              <p>You've learned comprehensive table implementation including semantic structure, colspan/rowspan techniques, accessibility features, and professional styling. Start creating organized, accessible data presentations!</p>
              
              <div className="html-action-buttons">
                <button 
                  className={`html-complete-button ${isCompleted ? 'html-completed' : ''}`}
                  onClick={() => setIsCompleted(true)}
                >
                  {isCompleted ? (
                    <>
                      <FaCheck className="html-button-icon" />
                      Lesson Completed
                    </>
                  ) : (
                    'Mark as Complete'
                  )}
                </button>
                
                {isCompleted && (
                  <Link to="/html-lists" className="html-next-lesson-link">
                    Continue to Next Lesson
                    <FaArrowRight className="html-link-icon" />
                  </Link>
                )}
              </div>
              
              <div className="html-quick-nav">
                <span>Need more examples? </span>
                <a 
                  href={videoUrl} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="html-nav-link"
                >
                  Watch Complete HTML Course
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </HTMLCourseNavProvider>
  );
}

export default HTMLLesson16;